// src/components/AdCampaignSection.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../App';
import { api } from '../../apiService';
import { toast } from 'react-toastify';
import ConfirmationModal from '../Common/ConfirmationModal';


// --- START: Helper Components & Functions ---

const CampaignIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
);

const getClientInitials = (name) => {
    if (!name) return '?';
    const words = name.split(' ');
    if (words.length > 1 && words[1]) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return words[0][0].toUpperCase();
};

const parseMaybeJson = (value, fallback) => {
    if (value === null || value === undefined) return fallback;
    if (typeof value !== 'string') return fallback;
    const trimmed = value.trim();
    if (!trimmed) return fallback;
    try { return JSON.parse(trimmed); } catch { return fallback; }
};

const normalizeTimingsArray = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean).map(String);
    if (typeof value === 'string') {
        const parsed = parseMaybeJson(value, null);
        if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
        return value.split(',').map(s => s.trim()).filter(Boolean);
    }
    return [];
};

const getCampaignTimingsText = (campaign, fallbackSubCampaigns = []) => {
    let subCampaigns = campaign?.subCampaigns;
    if (typeof subCampaigns === 'string') subCampaigns = parseMaybeJson(subCampaigns, []);
    const scList = Array.isArray(subCampaigns) ? subCampaigns : [];
    const parts = scList
        .map((sc) => normalizeTimingsArray(sc?.timings).join(', '))
        .filter(Boolean);
    if (parts.length > 0) return parts.join('; ');

    const fallbackParts = (Array.isArray(fallbackSubCampaigns) ? fallbackSubCampaigns : [])
        .map((sc) => normalizeTimingsArray(sc?.timings).join(', '))
        .filter(Boolean);
    if (fallbackParts.length > 0) return fallbackParts.join('; ');

    const adTimings = normalizeTimingsArray(campaign?.adTimings);
    return adTimings.join(', ');
};

const toMoney = (value) => {
    const n = Number(value);
    const safe = Number.isFinite(n) ? n : 0;
    const rounded = Math.round(safe * 100) / 100;
    return rounded.toLocaleString('en-PK', { maximumFractionDigits: 2 });
};

const createOrUpdateInvoiceForCampaign = async (campaignData, campaignId, client, allStations) => {
    if (!client) {
        toast.error("Client data is missing. Cannot create or update invoice.");
        return;
    }
    
    try {
        const existingInvoices = await api.get('invoices', { where: { campaignId: campaignId }, limit: 1 });
        const allPayments = await api.get('payments', { where: { campaignId: campaignId } });
        const totalPaid = allPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
        const pendingAmount = campaignData.totalCampaignAmount - totalPaid;
        
        let status = "Unpaid";
        if (pendingAmount <= 0) status = "Paid";
        else if (totalPaid > 0) status = "Partially Paid";
        
        const invoiceData = {
            clientId: client.id,
            clientName: client.name,
            clientCompany: client.companyName,
            clientAddress: client.companyAddress,
            campaignId: campaignId,
            campaignName: campaignData.mainCampaignName,
            subCampaigns: JSON.stringify(campaignData.subCampaigns),
            totalAmount: campaignData.totalCampaignAmount,
            stationId: campaignData.stationId,
            stationName: allStations.find(s => s.id === campaignData.stationId)?.name || 'N/A',
            invoiceDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
            status: status,
            totalPaid: totalPaid,
            pendingAmount: pendingAmount,
            invoiceMonth: new Date(campaignData.startDate).getMonth() + 1,
            invoiceYear: new Date(campaignData.startDate).getFullYear(),
            startDate: campaignData.startDate,
            endDate: campaignData.endDate,
        };

        if (existingInvoices.length > 0) {
            await api.put('invoices', existingInvoices[0].id, invoiceData);
        } else {
            await api.post('invoices', invoiceData);
        }

        // Handle public invoices if needed
    } catch (error) {
        console.error("Error creating/updating invoice:", error);
        toast.error("Failed to sync invoice with campaign changes.");
    }
};

// --- END: Helper Components & Functions ---


// --- START: Internal Components for AdCampaignSection ---

function ClientCampaignsView({ client, onBack, onEdit, onRenew }) {
    const [deletingCampaign, setDeletingCampaign] = useState(null);
    const { user } = useAppContext();
    const [campaigns, setCampaigns] = useState([]);
    const [payments, setPayments] = useState([]);
    const [subCampaignsByCampaignId, setSubCampaignsByCampaignId] = useState({});
    const [loading, setLoading] = useState(true);

    const fetchCampaignsAndPayments = useCallback(async () => {
        if (!client) return;
        setLoading(true);
        try {
            const clientCampaigns = await api.get('ad_campaigns', { where: { clientId: client.id }, orderBy: 'startDate DESC' });
            setCampaigns(clientCampaigns);

            const campaignIds = clientCampaigns.map(c => c.id).filter(Boolean);
            if (campaignIds.length > 0) {
                const allPayments = await api.get('payments');
                setPayments(allPayments.filter(p => campaignIds.includes(p.campaignId)));
                const subRows = await api.get('sub_campaigns', { where: { campaignId: { in: campaignIds } } }).catch(() => []);
                const byCampaign = {};
                (subRows || []).forEach((row) => {
                    const cid = row?.campaignId;
                    if (!cid) return;
                    if (!byCampaign[cid]) byCampaign[cid] = [];
                    byCampaign[cid].push({
                        name: row?.name,
                        timings: normalizeTimingsArray(row?.timings),
                    });
                });
                setSubCampaignsByCampaignId(byCampaign);
            } else {
                setPayments([]);
                setSubCampaignsByCampaignId({});
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to load client campaigns.");
        } finally {
            setLoading(false);
        }
    }, [client]);

    useEffect(() => {
        fetchCampaignsAndPayments();
    }, [fetchCampaignsAndPayments]);

    const handleDeleteCampaign = async () => {
        if (!deletingCampaign) return;
        toast.info("Deleting campaign and related data...");
        try {
            await api.delete('ad_campaigns', deletingCampaign.id);
            
            const existingInvoices = await api.get('invoices', { where: { campaignId: deletingCampaign.id } });
            if (existingInvoices.length > 0) {
                await api.delete('invoices', existingInvoices[0].id);
            }
    
            toast.success(`Campaign "${deletingCampaign.mainCampaignName || deletingCampaign.campaignName}" and its invoice have been deleted.`);
            fetchCampaignsAndPayments();
        } catch (err) {
            toast.error("Failed to delete campaign.");
            console.error("Error deleting campaign:", err);
        } finally {
            setDeletingCampaign(null);
        }
    };

    if (!client) {
        return (
            <div className="container mx-auto">
                <p>Client not found. Please go back.</p>
                <button onClick={onBack} className="text-rose-600 font-semibold">Back</button>
            </div>
        );
    }
    
    const groupedCampaigns = campaigns.reduce((acc, campaign) => {
        const monthYear = new Date(campaign.startDate).toLocaleString('default', { month: 'long', year: 'numeric' });
        if (!acc[monthYear]) { acc[monthYear] = []; }
        acc[monthYear].push(campaign);
        return acc;
    }, {});

    const sortedMonths = Object.keys(groupedCampaigns).sort((a, b) => new Date(b) - new Date(a));

    return (
        <div className="container mx-auto">
            <ConfirmationModal
                isOpen={!!deletingCampaign}
                onClose={() => setDeletingCampaign(null)}
                onConfirm={handleDeleteCampaign}
                title="Delete Campaign"
            >
                Are you sure you want to delete the campaign <strong>{deletingCampaign?.mainCampaignName || deletingCampaign?.campaignName}</strong>? 
                This will also delete its associated invoice and cannot be undone.
            </ConfirmationModal>

             <div className="mb-8">
                <button onClick={onBack} className="text-rose-600 font-semibold flex items-center gap-2 mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Back to Client List
                </button>
                <h1 className="text-4xl font-bold text-gray-800">{client.name}'s Campaigns</h1>
                <p className="text-gray-500 mt-1">{client.companyName}</p>
            </div>
            {loading ? <p>Loading campaigns...</p> : 
            sortedMonths.length > 0 ? sortedMonths.map(month => (
                <div key={month} className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-700 mb-4">{month}</h2>
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-500">
                                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3">Campaign</th>
                                        <th className="px-6 py-3">Duration</th>
                                        <th className="px-6 py-3 hidden lg:table-cell">Timings</th>
                                        <th className="px-6 py-3 text-right">Amount</th>
                                        <th className="px-6 py-3 text-right hidden md:table-cell">Received</th>
                                        <th className="px-6 py-3 text-right hidden md:table-cell">Pending</th>
                                        <th className="px-6 py-3 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedCampaigns[month].map(campaign => {
                                        const campaignPayments = payments.filter(p => p.campaignId === campaign.id);
                                        const totalPaid = campaignPayments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
                                        const pendingAmount = (Number(campaign.totalCampaignAmount) || 0) - totalPaid;
                                        const timingsText = getCampaignTimingsText(campaign, subCampaignsByCampaignId[campaign.id]);
                                        
                                        return (
                                            <tr key={campaign.id} className="bg-white border-b hover:bg-gray-50">
                                                <td className="px-6 py-4">
                                                    <div className="font-semibold text-gray-800">{campaign.mainCampaignName || campaign.campaignName}</div>
                                                    <div className="text-xs text-gray-500">{client?.name || 'Unknown Client'}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                                                    {new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}
                                                </td>
                                                <td className="px-6 py-4 text-xs text-gray-500 hidden lg:table-cell">
                                                    {timingsText || '--'}
                                                </td>
                                                <td className="px-6 py-4 font-bold text-gray-800 text-right">PKR {toMoney(campaign.totalCampaignAmount)}</td>
                                                <td className="px-6 py-4 font-semibold text-green-600 text-right hidden md:table-cell">PKR {toMoney(totalPaid)}</td>
                                                <td className="px-6 py-4 font-semibold text-red-600 text-right hidden md:table-cell">PKR {toMoney(pendingAmount)}</td>
                                                <td className="px-6 py-4 text-center whitespace-nowrap">
                                                    <button onClick={() => onEdit(campaign)} className="text-yellow-600 hover:text-yellow-800 font-semibold no-underline">Edit</button>
                                                    <span className="mx-1 text-gray-300">|</span>
                                                    <button onClick={() => onRenew(campaign)} className="text-green-600 hover:text-green-800 font-semibold no-underline">Renew</button>
                                                    {user.role === 'admin' && (
                                                        <>
                                                            <span className="mx-1 text-gray-300">|</span>
                                                            <button onClick={() => setDeletingCampaign(campaign)} className="text-red-600 hover:text-red-800 font-semibold no-underline">Delete</button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )) : (
                <div className="text-center p-8 text-gray-500 bg-white rounded-2xl shadow-lg">
                    No campaigns found for this client.
                </div>
            )}
        </div>
    );
}

function AdCampaignForm({ onBack, campaignToEdit, clients }) {
    const { currentStationId, allStations } = useAppContext();
    const [clientId, setClientId] = useState('');
    const [clientSearch, setClientSearch] = useState('');
    const [mainCampaignName, setMainCampaignName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [totalCampaignAmount, setTotalCampaignAmount] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [subCampaigns, setSubCampaigns] = useState([{ name: '', spots: 1, timings: [''] }]);

    useEffect(() => {
        let mounted = true;
        const hydrate = async () => {
            if (!campaignToEdit) return;
            setMainCampaignName(campaignToEdit.mainCampaignName || campaignToEdit.campaignName || '');
            setClientId(campaignToEdit.clientId || '');
            setStartDate(campaignToEdit.startDate ? new Date(campaignToEdit.startDate).toISOString().split('T')[0] : '');
            setEndDate(campaignToEdit.endDate ? new Date(campaignToEdit.endDate).toISOString().split('T')[0] : '');
            setTotalCampaignAmount(campaignToEdit.totalCampaignAmount || '');
            
            if (campaignToEdit.subCampaigns) {
                let scData = campaignToEdit.subCampaigns;
                if (typeof scData === 'string') {
                    try { scData = JSON.parse(scData); } catch { scData = []; }
                }
                const safeScData = Array.isArray(scData) ? scData : [];
                setSubCampaigns(safeScData.map(sc => ({
                    ...sc,
                    spots: sc.timings.length
                })));
            } else {
                const legacy = await api.get('sub_campaigns', { where: { campaignId: campaignToEdit.id } }).catch(() => []);
                if (!mounted) return;
                if (legacy.length > 0) {
                    setSubCampaigns(legacy.map(sc => {
                        const timings = normalizeTimingsArray(sc.timings);
                        return { name: sc.name || (campaignToEdit.campaignName || ''), spots: timings.length || 1, timings: timings.length > 0 ? timings : [''] };
                    }));
                } else {
                    const timings = normalizeTimingsArray(campaignToEdit.adTimings);
                    setSubCampaigns([{ 
                        name: campaignToEdit.campaignName || '', 
                        spots: campaignToEdit.dailySpots || timings.length || 1, 
                        timings: timings.length > 0 ? timings : [''] 
                    }]);
                }
            }
        };
        hydrate();
        return () => { mounted = false; };
    }, [campaignToEdit]);

    const handleAddPart = () => setSubCampaigns([...subCampaigns, { name: '', spots: 1, timings: [''] }]);

    const handleRemovePart = (index) => {
        if (subCampaigns.length <= 1) return;
        setSubCampaigns(subCampaigns.filter((_, i) => i !== index));
    };

    const handlePartChange = (index, field, value) => {
        const newParts = [...subCampaigns];
        const part = newParts[index];
        part[field] = value;
        if (field === 'spots') {
            const spots = parseInt(value) || 0;
            part.timings = Array(spots).fill('').map((_, i) => part.timings[i] || '');
        }
        setSubCampaigns(newParts);
    };
    
    const handleTimingChange = (partIndex, timeIndex, value) => {
        const newParts = [...subCampaigns];
        newParts[partIndex].timings[timeIndex] = value;
        setSubCampaigns(newParts);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        toast.info("Saving campaign and processing invoice...");

        const finalSubCampaigns = subCampaigns.map(sc => ({
            name: sc.name,
            timings: sc.timings.filter(t => t)
        }));

        try {
            const dataToSave = {
                clientId, stationId: currentStationId, mainCampaignName,
                startDate,
                endDate,
                totalCampaignAmount: parseFloat(totalCampaignAmount),
                subCampaigns: JSON.stringify(finalSubCampaigns), 
                isActive: true, 
                updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };
            
            let campaignId;
            const client = clients.find(c => c.id === clientId);

            if (campaignToEdit && campaignToEdit.id) {
                campaignId = campaignToEdit.id;
                await api.put('ad_campaigns', campaignId, dataToSave);
            } else {
                dataToSave.createdAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
                const res = await api.post('ad_campaigns', dataToSave);
                campaignId = res.id;
            }

            await createOrUpdateInvoiceForCampaign(dataToSave, campaignId, client, allStations);
            
            toast.success('Campaign and invoice saved successfully!');
            onBack();

        } catch (err) {
            toast.error("Failed to save campaign.");
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container mx-auto">
             <div className="mb-8">
                <button onClick={onBack} className="text-rose-600 font-semibold flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Back to Campaign List
                </button>
                <h1 className="text-4xl font-bold text-gray-800 mt-2">{campaignToEdit ? 'Edit Campaign' : 'Add New Campaign'}</h1>
            </div>
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl shadow-lg space-y-6">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label htmlFor="clientSearch" className="block text-sm font-medium text-gray-700">Client Search</label>
                        <input id="clientSearch" type="text" value={clientSearch} onChange={e => setClientSearch(e.target.value)} placeholder="Search by name / company / contact..." className="mt-1 block w-full p-3 bg-gray-50 rounded-md" />
                        <label htmlFor="clientId" className="block text-sm font-medium text-gray-700 mt-3">Client</label>
                        <select id="clientId" value={clientId} onChange={e => setClientId(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" required>
                            <option value="">Select Client</option>
                            {clients
                                .filter(c => {
                                    const q = clientSearch.trim().toLowerCase();
                                    if (!q) return true;
                                    const name = String(c.name || '').toLowerCase();
                                    const company = String(c.companyName || '').toLowerCase();
                                    const contact = String(c.contact || '').toLowerCase();
                                    return name.includes(q) || company.includes(q) || contact.includes(q);
                                })
                                .map(c => <option key={c.id} value={c.id}>{c.name} {c.companyName ? `(${c.companyName})` : ''}</option>)
                            }
                        </select>
                    </div>
                    <div><label htmlFor="stationId" className="block text-sm font-medium text-gray-700">Station</label><input type="text" value={allStations.find(s=>s.id === currentStationId)?.name || ''} className="mt-1 block w-full p-3 bg-gray-200 rounded-md" readOnly/></div>
                    <div className="md:col-span-2"><label htmlFor="mainCampaignName" className="block text-sm font-medium text-gray-700">Main Campaign/Package Name</label><input type="text" id="mainCampaignName" value={mainCampaignName} onChange={e => setMainCampaignName(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" required /></div>
                    <div><label htmlFor="startDate" className="block text-sm font-medium text-gray-700">Start Date</label><input type="date" id="startDate" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" required /></div>
                    <div><label htmlFor="endDate" className="block text-sm font-medium text-gray-700">End Date</label><input type="date" id="endDate" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" required /></div>
                    <div className="md:col-span-2"><label htmlFor="totalCampaignAmount" className="block text-sm font-medium text-gray-700">Total Amount (for entire package)</label><input type="number" id="totalCampaignAmount" value={totalCampaignAmount} onChange={e => setTotalCampaignAmount(e.target.value)} className="mt-1 block w-full p-3 bg-gray-50 rounded-md" required /></div>
                 </div>

                 <div className="space-y-6 pt-6 border-t">
                    <h3 className="text-lg font-bold text-gray-700">Campaign Parts</h3>
                    {subCampaigns.map((part, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg border relative">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Part #{index + 1} Name</label>
                                    <input type="text" value={part.name} onChange={e => handlePartChange(index, 'name', e.target.value)} className="mt-1 w-full p-2 border rounded-md" required/>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Daily Spots</label>
                                    <input type="number" value={part.spots} onChange={e => handlePartChange(index, 'spots', e.target.value)} className="mt-1 w-full p-2 border rounded-md" min="1" required/>
                                </div>
                            </div>
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700">Ad Timings</label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-1">
                                    {part.timings.map((time, timeIndex) => (
                                        <input key={timeIndex} type="time" value={time} onChange={e => handleTimingChange(index, timeIndex, e.target.value)} className="w-full p-2 border rounded-md" required/>
                                    ))}
                                </div>
                            </div>
                            {subCampaigns.length > 1 && (
                                <button type="button" onClick={() => handleRemovePart(index)} className="absolute top-2 right-2 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center font-bold text-sm">X</button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={handleAddPart} className="bg-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-green-700">+ Add Another Part</button>
                 </div>

                 <div className="flex justify-end pt-6 border-t">
                    <button type="submit" disabled={isSubmitting} className="bg-rose-600 text-white font-bold py-3 px-8 rounded-lg shadow-lg hover:bg-rose-700 transition-all disabled:bg-gray-400">
                        {isSubmitting ? 'Saving...' : 'Save Campaign'}
                    </button>
                </div>
            </form>
        </div>
    );
}

// --- END: Internal Components ---


// --- Main AdCampaignSection Component ---
function AdCampaignSection() {
    const { currentStationId } = useAppContext();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list');
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [searchTerm, setSearchTerm] = useState('');

    const [clientList, setClientList] = useState([]);

    // --- State for pagination ---
    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalClientsWithCampaigns, setTotalClientsWithCampaigns] = useState(0);

    // Fetch all clients for the station
    useEffect(() => {
        if (!currentStationId || currentStationId === 'all') {
            setClients([]);
            return;
        }
        const fetchClients = async () => {
            const results = await api.get('clients', { where: { stationId: currentStationId } });
            setClients(results);
        };
        fetchClients();
    }, [currentStationId]);

    // Fetch clients with campaigns for the list view with pagination
    const fetchClientsWithCampaigns = useCallback(async () => {
        if (currentStationId === 'all' || !currentStationId) {
            setClientList([]);
            setLoading(false);
            return;
        }
        setLoading(true);

        const startOfMonth = new Date(filterYear, filterMonth - 1, 1).toISOString().split('T')[0];
        const endOfMonth = new Date(filterYear, filterMonth, 0).toISOString().split('T')[0];
        
        try {
            const campaignsData = await api.get('ad_campaigns', { 
                where: { stationId: currentStationId } 
            });

            const filteredCampaigns = campaignsData.filter(c => {
                const d = new Date(c.startDate).toISOString().split('T')[0];
                return d >= startOfMonth && d <= endOfMonth;
            });

            const clientCampaignCounts = filteredCampaigns.reduce((acc, campaign) => {
                const clientId = campaign.clientId;
                if (!acc[clientId]) {
                    acc[clientId] = 0;
                }
                acc[clientId]++;
                return acc;
            }, {});

            let uniqueClientIds = Object.keys(clientCampaignCounts);

            // Apply search filter on client names
            if (searchTerm) {
                const lowerSearchTerm = searchTerm.toLowerCase();
                const filteredClientDetails = clients.filter(c => uniqueClientIds.includes(c.id) && c.name.toLowerCase().includes(lowerSearchTerm));
                uniqueClientIds = filteredClientDetails.map(c => c.id);
            }
            
            setTotalClientsWithCampaigns(uniqueClientIds.length);

            // Manual pagination on the client ID array
            const start = (currentPage - 1) * pageSize;
            const end = start + pageSize;
            const paginatedClientIds = uniqueClientIds.slice(start, end);

            const clientsWithCampaigns = clients
                .filter(client => paginatedClientIds.includes(client.id))
                .map(client => ({
                    ...client,
                    campaignCount: clientCampaignCounts[client.id] || 0
                }))
                .sort((a,b) => (a.name > b.name) ? 1 : -1); // Sort alphabetically

            setClientList(clientsWithCampaigns);
            
        } catch (err) {
            console.error("API Error:", err);
            toast.error("Failed to fetch campaigns.");
        } finally {
            setLoading(false);
        }
    }, [currentStationId, filterYear, filterMonth, searchTerm, currentPage, pageSize, clients]);

    useEffect(() => {
        fetchClientsWithCampaigns();
    }, [fetchClientsWithCampaigns]);

    const handleEditCampaign = (campaign) => { setEditingCampaign(campaign); setView('form'); };
    
    const handleRenewCampaign = (campaign) => {
        const renewedCampaign = { ...campaign, mainCampaignName: `${campaign.mainCampaignName || campaign.campaignName} (Renewed)`, startDate: '', endDate: '' };
        delete renewedCampaign.id;
        delete renewedCampaign.campaignName; delete renewedCampaign.adTimings; delete renewedCampaign.dailySpots;
        setEditingCampaign(renewedCampaign);
        setView('form');
    };
    
    const handleNextPage = () => {
        setCurrentPage(prev => prev + 1);
    };

    const handlePrevPage = () => {
        setCurrentPage(prev => prev - 1);
    };
    
    const selectedClient = clients.find(c => c.id === selectedClientId);

    if (view === 'form') {
        return <AdCampaignForm clients={clients} onBack={() => { setView('list'); setEditingCampaign(null); }} campaignToEdit={editingCampaign} />;
    }
    if (selectedClientId) {
        return <ClientCampaignsView client={selectedClient} onBack={() => setSelectedClientId(null)} onEdit={handleEditCampaign} onRenew={handleRenewCampaign} />;
    }

    const pageStart = (currentPage - 1) * pageSize + 1;
    const pageEnd = Math.min(currentPage * pageSize, totalClientsWithCampaigns);

    return (
        <div className="container mx-auto">
            <div className="mb-8 flex justify-between items-center">
                <div>
                    <h1 className="text-4xl font-bold text-gray-800">Client Campaigns</h1>
                    <p className="text-gray-500 mt-1">Select a client to view their campaigns.</p>
                </div>
                <button onClick={() => { setEditingCampaign(null); setView('form'); }} disabled={currentStationId === 'all'} className="bg-rose-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-rose-700 transition-all disabled:bg-gray-400">
                    + Add New Campaign
                </button>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg mb-8">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                     <div className="md:col-span-2">
                        <label htmlFor="search-campaign" className="block text-sm font-medium text-gray-600">Search Client or Campaign</label>
                        <input type="text" id="search-campaign" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Client or Campaign Name..." className="mt-1 block w-full bg-gray-50 border-gray-300 rounded-md shadow-sm p-3 font-semibold"/>
                    </div>
                    <div>
                        <label htmlFor="month-filter" className="block text-sm font-medium text-gray-600">Month</label>
                        <select id="month-filter" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="mt-1 block w-full bg-gray-50 border-gray-300 rounded-md shadow-sm p-3 font-semibold">
                            {Array.from({ length: 12 }, (_, i) => <option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="year-filter" className="block text-sm font-medium text-gray-600">Year</label>
                        <select id="year-filter" value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} className="mt-1 block w-full bg-gray-50 border-gray-300 rounded-md shadow-sm p-3 font-semibold">
                            {Array.from({ length: 10 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}
                        </select>
                    </div>
                </div>
            </div>
            {loading ? <p>Loading clients...</p> : 
             currentStationId === 'all' ? <div className="text-center p-8 text-gray-500 bg-white rounded-2xl shadow-lg">Please select a station to view campaigns.</div> : 
             clientList.length > 0 ? (
                <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {clientList.map(client => (
                        <div key={client.id} onClick={() => setSelectedClientId(client.id)} className="bg-white rounded-2xl shadow-md p-5 flex flex-col justify-between cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                            <div className="flex-grow">
                                <div className="flex items-center gap-4">
                                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-rose-100 text-rose-600 font-bold text-lg">{getClientInitials(client.name)}</div>
                                    <div className="overflow-hidden">
                                        <h3 className="text-lg font-bold text-gray-800 truncate" title={client.name}>{client.name}</h3>
                                        <p className="text-sm text-gray-500 truncate" title={client.companyName}>{client.companyName || 'No Company'}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 text-gray-600"><CampaignIcon /><span>Campaigns</span></div>
                                <span className="font-bold text-gray-800 bg-gray-100 px-2 py-1 rounded">{client.campaignCount}</span>
                            </div>
                        </div>
                    ))}
                </div>
                {/* --- NEW: PAGINATION CONTROLS --- */}
                <div className="mt-8 p-4 flex justify-between items-center bg-white rounded-2xl shadow-lg">
                    <div className="flex items-center gap-2">
                        <label htmlFor="pageSize" className="text-sm font-medium text-gray-700">Show:</label>
                        <select 
                            id="pageSize" 
                            value={pageSize} 
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            className="p-2 bg-gray-50 border-gray-300 rounded-md shadow-sm font-semibold"
                        >
                            <option value="20">20</option>
                            <option value="40">40</option>
                            <option value="60">60</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                        Showing {pageStart} - {pageEnd} of {totalClientsWithCampaigns}
                    </span>
                    <div className="flex gap-2">
                        <button 
                            onClick={handlePrevPage} 
                            disabled={currentPage === 1 || loading}
                            className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 rounded-md shadow disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button 
                            onClick={handleNextPage} 
                            disabled={pageEnd >= totalClientsWithCampaigns || loading}
                            className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 rounded-md shadow disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
                </>
            ) : ( <p className="text-center p-8 text-gray-500 bg-white rounded-2xl shadow-lg">No clients with active campaigns found for the selected criteria.</p> )}
        </div>
    );
}

export default AdCampaignSection;
